import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FiSettings, FiTag, FiUsers, FiBell, FiKey, FiSliders, FiSave, FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff } from 'react-icons/fi';
import { useToast } from '../contexts/ToastContext';
import { API_ENDPOINTS } from '../config/api';
import apiClient, { endpoints } from '../api/client';
import { Switch } from '../components/Switch';

const Settings = () => {
  const { isSuperAdmin } = useAuth();
  const { showError, showSuccess } = useToast();
  const [activeTab, setActiveTab] = useState('notifications');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    tags: [],
    roles: {},
    reminders: { email: true, sms: false, whatsapp: false },
    notifications: { email: true, sms: false, whatsapp: false },
    apiKeys: { postmark_api_key: '', twilio_account_sid: '', twilio_auth_token: '', whatsapp_api_key: '' },
    systemPreferences: {
      reminder_schedule: [90, 60, 30, 10, 1],
      reminder_time: '09:00',
      timezone: 'America/New_York',
      date_format: 'MM/DD/YYYY',
      time_format: '12h',
      default_language: 'en'
    },
    branding: { logo: '', primary_color: '#FF0404', company_name: 'Premier Choice CRM' }
  });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await apiClient.get(API_ENDPOINTS.SETTINGS, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showError('Failed to fetch settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = async (updatedData) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await apiClient.put(API_ENDPOINTS.SETTINGS, updatedData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSettings(response.data.data);
        showSuccess('Settings updated successfully');
        return true;
      } else {
        showError(response.data.message || 'Failed to update settings');
        return false;
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      showError('Failed to update settings');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Redirect if not super admin and fetch settings
  useEffect(() => {
    if (!isSuperAdmin()) {
      window.location.href = '/dashboard';
    } else {
      fetchSettings();
    }
  }, [isSuperAdmin]);

  const tabs = [
    { id: 'notifications', name: 'Notification Toggles', icon: FiBell },
    { id: 'reminders', name: 'Reminder Toggles', icon: FiBell },
    { id: 'system', name: 'System Preferences', icon: FiSliders }
  ];

  if (!isSuperAdmin()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 page-content">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center space-x-3">
            <FiSettings className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Settings & System Configuration</h1>
          </div>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Manage system settings, tags, roles, notifications, and integrations.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-5">
          <div className="flex">
            <FiBell className="h-5 w-5 text-blue-400 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Manual Reminder Trigger
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Fine-tune system preferences, notification channels, reminder channels, and reminder dates for a seamless experience.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-wrap sm:space-x-8 px-4 sm:px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${activeTab === tab.id
                      ? 'border-red-500 text-red-600 bg-red-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                      } whitespace-nowrap py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 transition-all duration-200 rounded-t-md`}
                  >
                    <Icon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                    <span className="hidden sm:inline">{tab.name}</span>
                    <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-6 max-h-[calc(100vh-220px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <div className="space-y-4 sm:space-y-6">
              {activeTab === 'notifications' && (
                <NotificationToggles
                  settings={settings}
                  updateSettings={updateSettings}
                  loading={loading}
                  saving={saving}
                  showError={showError}
                  showSuccess={showSuccess}
                />
              )}
              {activeTab === 'reminders' && (
                <ReminderToggles
                  settings={settings}
                  updateSettings={updateSettings}
                  loading={loading}
                  saving={saving}
                  showError={showError}
                  showSuccess={showSuccess}
                />
              )}
              {activeTab === 'system' && (
                <SystemPreferences
                  settings={settings}
                  updateSettings={updateSettings}
                  loading={loading}
                  showError={showError}
                  showSuccess={showSuccess}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReminderToggles = ({ settings, updateSettings, loading: parentLoading, saving, showError, showSuccess }) => {
  const [localLoading, setLocalLoading] = useState(false);
  const loading = parentLoading || localLoading;

  const handleToggle = async (key) => {
    setLocalLoading(true);
    try {
      const updatedReminders = { ...settings.reminders, [key]: !settings.reminders[key] };
      await updateSettings({ reminders: updatedReminders });
      showSuccess('Reminder settings updated successfully');
    } catch (error) {
      showError('Failed to update reminder settings');
    } finally {
      setLocalLoading(false);
    }
  };

  const reminderTypes = [
    {
      key: 'email',
      title: 'Email Reminders',
      description: 'Send certificate expiration reminders via email',
      icon: '📧',
      enabled: settings.reminders?.email ?? false
    },
    {
      key: 'sms',
      title: 'SMS Reminders',
      description: 'Send certificate expiration reminders via SMS',
      icon: '📱',
      enabled: settings.reminders?.sms ?? false
    },
    {
      key: 'whatsapp',
      title: 'WhatsApp Reminders',
      description: 'Send certificate expiration reminders via WhatsApp (Coming Soon)',
      icon: '💬',
      enabled: settings.reminders?.whatsapp ?? false
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Reminder Settings</h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure how and when reminders are sent to clients.
        </p>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="space-y-6">
            {/* Reminders Section */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Certificate Reminders</h4>
              <div className="space-y-4">
                {reminderTypes.map((type) => (
                  <div key={type.key} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{type.icon}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{type.title}</div>
                        <div className="text-sm text-gray-500">{type.description}</div>
                      </div>
                    </div>
                    <Switch value={type.enabled} onClick={() => handleToggle(type.key)} loading={saving} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Notification Toggles Component
const NotificationToggles = ({ settings, updateSettings, loading: parentLoading, saving, showError, showSuccess }) => {
  const [localLoading, setLocalLoading] = useState(false);
  const loading = parentLoading || localLoading;

  const handleToggle = async (key) => {
    setLocalLoading(true);
    try {
      const updatedNotifications = { ...settings.notifications, [key]: !settings.notifications[key] };
      await updateSettings({ notifications: updatedNotifications });
      showSuccess('Notification settings updated successfully');
    } catch (error) {
      showError('Failed to update notification settings');
    } finally {
      setLocalLoading(false);
    }
  };

  const notificationTypes = [
    {
      key: 'email',
      title: 'Email Notifications',
      description: 'Send system notifications and updates via email',
      icon: '🔔',
      enabled: settings.notifications.email
    },
    {
      key: 'sms',
      title: 'SMS Notifications',
      description: 'Send system notifications and updates via SMS',
      icon: '📲',
      enabled: settings.notifications.sms
    },
    {
      key: 'whatsapp',
      title: 'WhatsApp Notifications',
      description: 'Send system notifications and updates via WhatsApp (Coming Soon)',
      icon: '💬',
      enabled: settings.notifications.whatsapp
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Notification Settings</h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure how and when notifications are sent to clients.
        </p>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="space-y-6">

            {/* General Notifications Section */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">System Notifications</h4>
              <div className="space-y-4">
                {notificationTypes.map((type) => (
                  <div key={type.key} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{type.icon}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{type.title}</div>
                        <div className="text-sm text-gray-500">{type.description}</div>
                      </div>
                    </div>
                    <Switch value={type.enabled} onClick={() => handleToggle(type.key)} loading={saving} />
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Settings */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">Additional Settings</h4>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      API Configuration Required
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        To enable SMS and WhatsApp notifications, configure your API keys in the
                        <span className="font-medium"> API Keys & Integrations</span> section.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SystemPreferences = ({ settings, updateSettings, loading: parentLoading, showError, showSuccess }) => {
  const systemPreferences = settings?.systemPreferences || {};
  const branding = settings?.branding || {};
  const [localLoading, setLocalLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const loading = parentLoading || localLoading;

  const handlePreferenceChange = async (key, value) => {
    setLocalLoading(true);
    try {
      if (key === 'primary_color' || key === 'company_name' || key === 'logo_url') {
        await updateSettings({ branding: { ...branding, [key]: value } });
      } else {
        await updateSettings({ systemPreferences: { ...systemPreferences, [key]: value } });
      }
      showSuccess('Preference updated successfully');
    } catch (error) {
      showError('Failed to update preference');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleReminderScheduleChange = async (index, value) => {
    const currentSchedule = systemPreferences.reminder_schedule || [];
    const newSchedule = [...currentSchedule];
    newSchedule[index] = parseInt(value) || 0;

    setLocalLoading(true);
    try {
      await updateSettings({ systemPreferences: { ...systemPreferences, reminder_schedule: newSchedule } });
      showSuccess('Reminder schedule updated successfully');
    } catch (error) {
      showError('Failed to update reminder schedule');
    } finally {
      setLocalLoading(false);
    }
  };

  const addReminderDay = async () => {
    const currentSchedule = systemPreferences.reminder_schedule || [90, 60, 30, 10, 1];
    if (currentSchedule.length < 10) {
      setLocalLoading(true);
      try {
        const newSchedule = [...currentSchedule, 7];
        await updateSettings({ systemPreferences: { ...systemPreferences, reminder_schedule: newSchedule } });
        showSuccess('Reminder day added successfully');
      } catch (error) {
        showError('Failed to add reminder day');
      } finally {
        setLocalLoading(false);
      }
    }
  };

  const removeReminderDay = async (index) => {
    const currentSchedule = systemPreferences.reminder_schedule || [90, 60, 30, 10, 1];
    if (currentSchedule.length > 1) {
      setLocalLoading(true);
      try {
        const newSchedule = currentSchedule.filter((_, i) => i !== index);
        await updateSettings({ systemPreferences: { ...systemPreferences, reminder_schedule: newSchedule } });
        showSuccess('Reminder day removed successfully');
      } catch (error) {
        showError('Failed to remove reminder day');
      } finally {
        setLocalLoading(false);
      }
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) { // 2MB limit
      showError('File size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const token = localStorage.getItem('token');
      const response = await apiClient.post(API_ENDPOINTS.SETTINGS_UPLOAD_LOGO, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        await updateSettings({ branding: { ...branding, logo_url: response.data.logoUrl } });
        showSuccess('Logo uploaded successfully');
      } else {
        showError(response.data.message || 'Failed to upload logo');
      }
    } catch (error) {
      showError('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const predefinedColors = [
    '#FF0404', '#1976d2', '#388e3c', '#f57c00',
    '#7b1fa2', '#5d4037', '#455a64', '#e91e63'
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">System Preferences</h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure system-wide settings and branding options.
        </p>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="space-y-8">
            {/* Reminder Settings */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Reminder Settings</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reminder Schedule (days before expiration)
                  </label>
                  <div className="space-y-2">
                    {(systemPreferences.reminder_schedule).map((days, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={days}
                          onChange={(e) => handleReminderScheduleChange(index, e.target.value)}
                          className="w-20 border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 sm:text-sm"
                        />
                        <span className="text-sm text-gray-500">days</span>
                        {/* {(systemPreferences.reminder_schedule).length > 1 && (
                          <button
                            onClick={() => removeReminderDay(index)}
                            className="inline-flex items-center px-2 py-1 text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-lg transition-all duration-200"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        )} */}
                      </div>
                    ))}
                    {/* {(systemPreferences.reminder_schedule || [90, 60, 30, 10, 1]).length < 10 && (
                      <button
                        onClick={addReminderDay}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary btn-animate transition-all duration-200"
                      >
                        <FiPlus className="h-4 w-4 mr-2" />
                        Add Reminder
                      </button>
                    )} */}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Reminder Time</label>
                    <input
                      type="time"
                      value={systemPreferences.reminder_time || '09:00'}
                      onChange={(e) => handlePreferenceChange('reminder_time', e.target.value)}
                      className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 sm:text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Branding Settings */}
            {/* <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">Branding & Appearance</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company Name</label>
                  <input
                    type="text"
                    value={branding.company_name || ''}
                    onChange={(e) => handlePreferenceChange('company_name', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="color"
                      value={branding.primary_color || '#FF0404'}
                      onChange={(e) => handlePreferenceChange('primary_color', e.target.value)}
                      className="h-10 w-20 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <div className="flex space-x-2">
                      {predefinedColors.map((color) => (
                        <button
                          key={color}
                          onClick={() => handlePreferenceChange('primary_color', color)}
                          className={`w-8 h-8 rounded-full border-2 ${branding.primary_color === color ? 'border-gray-800' : 'border-gray-300'
                            }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo</label>
                  <div className="flex items-center space-x-4">
                    {branding.logo_url && (
                      <div className="flex-shrink-0">
                        <img
                          src={branding.logo_url}
                          alt="Company Logo"
                          className="h-16 w-16 object-contain border border-gray-300 rounded-lg"
                        />
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 disabled:opacity-50"
                      />
                      <p className="mt-1 text-xs text-gray-500">PNG, JPG, GIF up to 2MB</p>
                      {uploadingLogo && (
                        <div className="mt-2 flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                          <span className="text-sm text-gray-500">Uploading...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Format Settings */}
            {/* <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">Format Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date Format</label>
                  <select
                    value={systemPreferences.date_format || 'MM/DD/YYYY'}
                    onChange={(e) => handlePreferenceChange('date_format', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 sm:text-sm"
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Time Format</label>
                  <select
                    value={systemPreferences.time_format || '12h'}
                    onChange={(e) => handlePreferenceChange('time_format', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 sm:text-sm"
                  >
                    <option value="12h">12 Hour (AM/PM)</option>
                    <option value="24h">24 Hour</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Default Language</label>
                  <select
                    value={systemPreferences.default_language || 'en'}
                    onChange={(e) => handlePreferenceChange('default_language', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary sm:text-sm transition-all duration-200"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </div>
              </div>
            </div> */}

            {/* Loading indicator */}
            {/* {loading && (
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-primary btn-pulse mr-2"></div>
                  <span className="text-sm text-gray-500">Updating preferences...</span>
                </div>
              </div>
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;